import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { userAPI, configAPI, adminMgmtAPI } from '@/lib/api';
import { authAPI } from '@/lib/api';
import { ArrowLeft, User, Mail, Shield, Edit, Search, Plus, Pencil, Trash2, AlertTriangle, UserPlus, ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';

// ─── Hoisted helpers ────────────────────────────────────────────────────────────
// IMPORTANT: These must live OUTSIDE the `UserManagement` component to keep a
// stable component identity across renders. If they are re-declared inside the
// parent on every render, React will unmount/remount the whole subtree on any
// state update — which closes open Radix <Select> popups and drops clicks.

const DepartmentSelectStable = ({
  value,
  onChange,
  testId,
  excludeValues = [],
  departmentHierarchy,
  deptSearchQuery,
  setDeptSearchQuery,
}) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}>
      <SelectValue placeholder="Select department" />
    </SelectTrigger>
    <SelectContent className="max-h-80">
      <div className="px-2 pb-2 sticky top-0 bg-white z-10">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search departments..."
            value={deptSearchQuery}
            onChange={(e) => setDeptSearchQuery(e.target.value)}
            className="pl-8 h-9"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
      </div>
      {Object.entries(departmentHierarchy)
        .filter(([category]) => category !== 'Locations')
        .map(([category, data]) => {
          const filtered = data.departments.filter((d) =>
            (!deptSearchQuery.trim() || d.toLowerCase().includes(deptSearchQuery.toLowerCase())) &&
            (d === value || !excludeValues.includes(d))
          );
          if (filtered.length === 0) return null;
          return (
            <SelectGroup key={category}>
              <SelectLabel className="text-xs font-bold text-[#E97832] uppercase tracking-wider bg-orange-50 py-2 px-2 -mx-1">
                {category}
              </SelectLabel>
              {filtered.map((dept) => (
                <SelectItem key={dept} value={dept} className="pl-4">{dept}</SelectItem>
              ))}
            </SelectGroup>
          );
        })}
    </SelectContent>
  </Select>
);

const MultiDepartmentPickerStable = ({
  count,
  departments,
  onCountChange,
  onDepartmentsChange,
  testIdPrefix,
  departmentHierarchy,
  deptSearchQuery,
  setDeptSearchQuery,
}) => {
  const handleCount = (v) => {
    const n = parseInt(v, 10) || 1;
    const next = (departments || []).slice(0, n);
    while (next.length < n) next.push('');
    onCountChange(n);
    onDepartmentsChange(next);
  };
  const handleSlotChange = (idx, val) => {
    const next = [...(departments || [])];
    next[idx] = val;
    onDepartmentsChange(next);
  };
  return (
    <div className="space-y-3 border border-blue-200 bg-blue-50/40 rounded-lg p-4">
      <div className="space-y-2">
        <Label className="text-sm">
          Number of Departments to Assign <span className="text-red-500">*</span>
        </Label>
        <Select value={String(count)} onValueChange={handleCount}>
          <SelectTrigger className="bg-white" data-testid={`${testIdPrefix}-dept-count`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
              <SelectItem key={n} value={String(n)} data-testid={`${testIdPrefix}-dept-count-option-${n}`}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500">
          Tickets from any of the selected departments will be routed to this Process Owner.
        </p>
      </div>
      <div className="space-y-2">
        <Label className="text-sm">Select Departments <span className="text-red-500">*</span></Label>
        <div className="space-y-2">
          {Array.from({ length: count }, (_, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-600 w-6">#{i + 1}</span>
              <div className="flex-1">
                <DepartmentSelectStable
                  value={(departments && departments[i]) || ''}
                  onChange={(v) => handleSlotChange(i, v)}
                  testId={`${testIdPrefix}-dept-slot-${i}`}
                  excludeValues={(departments || []).filter((_, idx) => idx !== i).filter(Boolean)}
                  departmentHierarchy={departmentHierarchy}
                  deptSearchQuery={deptSearchQuery}
                  setDeptSearchQuery={setDeptSearchQuery}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


/**
 * MainDeptAdminAccessCard — Manage Users → "Main Department Admin Access"
 *
 * Renders one row per admin with two multi-select pickers (Level 1 / Level 2
 * main departments) plus a "Fallback" toggle. All edits are persisted via
 * `/api/admin/main-dept-admins/{email}`. The picker options come from the
 * canonical catalog endpoint so we can never accept a typo.
 */
const MainDeptAdminAccessCard = ({ hasEditAccess }) => {
  const [admins, setAdmins] = useState([]);
  const [catalog, setCatalog] = useState([]);
  // Feb 2026: sub-departments per main, displayed as a small caption
  // under each main chip so admins can see EXACTLY which sub-departments
  // roll up into their L1/L2 CC coverage. Sourced from the SAME endpoint
  // that powers the Ticket Creator form so the display can never drift.
  const [subDepartments, setSubDepartments] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingEmail, setSavingEmail] = useState(null);
  const [error, setError] = useState(null);
  const [addingEmail, setAddingEmail] = useState('');
  const [addingName, setAddingName] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [catRes, listRes, formRes] = await Promise.allSettled([
        adminMgmtAPI.getMainDeptCatalog(),
        adminMgmtAPI.listMainDeptAdmins(),
        configAPI.getTicketFormConfig(),
      ]);
      if (catRes.status === 'fulfilled') {
        setCatalog(catRes.value?.data?.main_departments || []);
      }
      if (listRes.status === 'fulfilled') {
        setAdmins(listRes.value?.data?.admins || []);
      } else {
        // Abort/Cancel errors (React 18 double-effect deduped by our
        // axios request interceptor) are not real failures — the
        // superseding call will populate the data. Only surface a
        // banner for genuine network / HTTP failures.
        const reason = listRes.reason;
        const wasCancelled = (
          reason?.code === 'ERR_CANCELED'
          || reason?.name === 'CanceledError'
          || reason?.message === 'canceled'
        );
        if (!wasCancelled) {
          setError('Could not load admin assignments. Showing last known data.');
        }
      }
      if (formRes.status === 'fulfilled') {
        setSubDepartments(formRes.value?.data?.sub_departments || {});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const toggleDept = (admin, level, dept) => {
    const key = level === 1 ? 'level1_main_departments' : 'level2_main_departments';
    const next = new Set(admin[key] || []);
    if (next.has(dept)) next.delete(dept);
    else next.add(dept);
    return Array.from(next);
  };

  const updateAdmin = (email, patch) => {
    setAdmins((prev) => prev.map((a) => (a.email === email ? { ...a, ...patch } : a)));
  };

  const saveAdmin = async (admin) => {
    setSavingEmail(admin.email);
    try {
      // Sanitize sub-arrays before send: drop any sub whose parent main
      // is no longer in the same-level main array (happens when a main
      // is unchecked but its subs still exist in the local state).
      const cleanSubs = (levelKeyMains, subs) => {
        const activeMains = new Set(admin[levelKeyMains] || []);
        return (subs || []).filter((sub) => {
          for (const [main, subList] of Object.entries(subDepartments)) {
            if (subList.includes(sub)) return activeMains.has(main);
          }
          return false;
        });
      };
      const l1Subs = cleanSubs('level1_main_departments', admin.level1_sub_departments);
      const l2Subs = cleanSubs('level2_main_departments', admin.level2_sub_departments);
      const { data } = await adminMgmtAPI.upsertMainDeptAdmin(admin.email, {
        name: admin.name,
        designation: admin.designation,
        level1_main_departments: admin.level1_main_departments || [],
        level2_main_departments: admin.level2_main_departments || [],
        level1_sub_departments: l1Subs,
        level2_sub_departments: l2Subs,
        is_fallback: !!admin.is_fallback,
      });
      updateAdmin(admin.email, data);
      toast.success(`Saved access for ${admin.name || admin.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save assignment');
    } finally {
      setSavingEmail(null);
    }
  };

  const removeAdmin = async (email) => {
    if (!window.confirm(`Remove main-department access for ${email}? This does not delete the user — only their admin mapping.`)) return;
    try {
      await adminMgmtAPI.deleteMainDeptAdmin(email);
      setAdmins((prev) => prev.filter((a) => a.email !== email));
      toast.success('Assignment removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove assignment');
    }
  };

  const addNew = async () => {
    const email = addingEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid admin email');
      return;
    }
    setSavingEmail(email);
    try {
      const { data } = await adminMgmtAPI.upsertMainDeptAdmin(email, {
        name: addingName.trim() || email,
        level1_main_departments: [],
        level2_main_departments: [],
        is_fallback: false,
      });
      setAdmins((prev) => {
        const without = prev.filter((a) => a.email !== email);
        return [...without, data].sort((a, b) =>
          (a.name || a.email).toLowerCase().localeCompare((b.name || b.email).toLowerCase())
        );
      });
      setAddingEmail('');
      setAddingName('');
      toast.success(`Added ${email} — now assign their departments below.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add admin');
    } finally {
      setSavingEmail(null);
    }
  };

  return (
    <Card data-testid="main-dept-admin-access-card">
      <CardHeader>
        <CardTitle className="font-heading text-base">Main Department Admin Access</CardTitle>
        <CardDescription>
          Assign one or more <strong>main departments</strong> to each admin panel member. Level 1 admins receive CC on all ticket-status emails for their departments; Level 2 admins are CC&apos;d on SLA-breach escalations. The &quot;Fallback&quot; toggle catches any main department not explicitly assigned (typically CAO).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-6 text-center text-slate-500 text-sm">Loading assignments…</div>
        ) : admins.length === 0 ? (
          <div className="py-6 text-center text-slate-500 text-sm">No assignments yet. Use &quot;Add admin&quot; below to create one.</div>
        ) : (
          <div className="space-y-4">
            {admins.map((admin) => (
              <div
                key={admin.email}
                data-testid={`main-dept-admin-row-${admin.email}`}
                className="border border-slate-200 rounded-lg p-4 bg-white"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">{admin.name || admin.email}</div>
                    <div className="text-xs text-slate-500">{admin.email}{admin.designation ? ` · ${admin.designation}` : ''}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-2 py-1" data-testid={`fallback-toggle-${admin.email}`}>
                      <input
                        type="checkbox"
                        disabled={!hasEditAccess}
                        checked={!!admin.is_fallback}
                        onChange={(e) => updateAdmin(admin.email, { is_fallback: e.target.checked })}
                        className="w-3.5 h-3.5 accent-[#E97832]"
                      />
                      Fallback for all unassigned main depts
                    </label>
                  </div>
                </div>

                {[
                  { level: 1, label: 'Level 1 — CC for ticket emails' },
                  { level: 2, label: 'Level 2 — CC for SLA breach escalations' },
                ].map(({ level, label }) => {
                  const key = level === 1 ? 'level1_main_departments' : 'level2_main_departments';
                  const selected = new Set(admin[key] || []);
                  return (
                    <div key={level} className="mb-3">
                      <div className="text-xs font-semibold text-slate-700 mb-1.5">{label}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {catalog.map((dept) => {
                          const active = selected.has(dept);
                          return (
                            <button
                              key={dept}
                              type="button"
                              disabled={!hasEditAccess || savingEmail === admin.email}
                              data-testid={`l${level}-dept-chip-${admin.email}-${dept}`}
                              onClick={() => updateAdmin(admin.email, { [key]: toggleDept(admin, level, dept) })}
                              className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${
                                active
                                  ? 'bg-[#E97832] border-[#E97832] text-white'
                                  : 'bg-white border-slate-300 text-slate-700 hover:border-[#E97832]'
                              } ${!hasEditAccess ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                            >
                              {dept}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/*
                  Feb 2026: per-main "sub-department coverage" selector —
                  chips let admins narrow their CC coverage to specific
                  sub-departments. Empty chip-set under a main = wildcard
                  (admin gets CC'd for every sub under that main).
                */}
                {(() => {
                  const activeMains = Array.from(new Set([
                    ...(admin.level1_main_departments || []),
                    ...(admin.level2_main_departments || []),
                  ]));
                  if (activeMains.length === 0) return null;
                  return (
                    <div
                      className="mt-2 mb-3 bg-slate-50 border border-slate-200 rounded-md p-3"
                      data-testid={`sub-dept-coverage-${admin.email}`}
                    >
                      <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">
                        Sub-department CC selection <span className="normal-case text-slate-500 font-normal">(empty = CC for every sub under that main)</span>
                      </div>
                      <div className="space-y-2.5">
                        {activeMains.map((main) => {
                          const subs = subDepartments[main] || [];
                          if (subs.length === 0) return null;
                          const l1SubsSel = new Set(admin.level1_sub_departments || []);
                          const l2SubsSel = new Set(admin.level2_sub_departments || []);
                          const isL1Main = (admin.level1_main_departments || []).includes(main);
                          const isL2Main = (admin.level2_main_departments || []).includes(main);
                          return (
                            <div key={main} className="border-t border-slate-200 first:border-t-0 first:pt-0 pt-2">
                              <div className="text-xs font-medium text-slate-900 mb-1.5">{main}</div>
                              {[
                                { level: 1, key: 'level1_sub_departments', selected: l1SubsSel, active: isL1Main, label: 'L1 subs' },
                                { level: 2, key: 'level2_sub_departments', selected: l2SubsSel, active: isL2Main, label: 'L2 subs' },
                              ].map(({ level, key, selected, active, label }) => {
                                if (!active) return null;
                                return (
                                  <div key={level} className="flex items-start gap-2 mb-1.5">
                                    <span className="text-[10px] uppercase tracking-wider text-slate-500 mt-1 min-w-[52px]">{label}</span>
                                    <div className="flex flex-wrap gap-1">
                                      {subs.map((sub) => {
                                        const on = selected.has(sub);
                                        return (
                                          <button
                                            key={sub}
                                            type="button"
                                            disabled={!hasEditAccess || savingEmail === admin.email}
                                            data-testid={`l${level}-sub-chip-${admin.email}-${sub}`}
                                            onClick={() => {
                                              // Toggle THIS sub in the level-N sub array.
                                              const cur = new Set(admin[key] || []);
                                              if (on) cur.delete(sub); else cur.add(sub);
                                              updateAdmin(admin.email, { [key]: Array.from(cur) });
                                            }}
                                            className={`px-2 py-0.5 rounded-full border text-[11px] transition-colors ${
                                              on
                                                ? 'bg-slate-800 border-slate-800 text-white'
                                                : 'bg-white border-slate-300 text-slate-600 hover:border-slate-500'
                                            } ${!hasEditAccess ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                          >
                                            {sub}
                                          </button>
                                        );
                                      })}
                                      {selected.size === 0 && (
                                        <span className="text-[10px] text-slate-500 italic ml-1 mt-1">
                                          (all subs)
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                  <Button
                    size="sm"
                    disabled={!hasEditAccess || savingEmail === admin.email}
                    onClick={() => saveAdmin(admin)}
                    data-testid={`save-admin-btn-${admin.email}`}
                    className="bg-[#E97832] hover:bg-[#d56b2c] h-8"
                  >
                    {savingEmail === admin.email ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!hasEditAccess}
                    onClick={() => removeAdmin(admin.email)}
                    data-testid={`remove-admin-btn-${admin.email}`}
                    className="h-8"
                  >
                    Remove
                  </Button>
                  {admin.updated_at && (
                    <span className="text-xs text-slate-400 ml-auto">
                      Updated {new Date(admin.updated_at).toLocaleString()}
                      {admin.updated_by ? ` · by ${admin.updated_by}` : ''}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {hasEditAccess && (
          <div className="border-t border-slate-200 pt-4">
            <div className="text-xs font-semibold text-slate-700 mb-2">Add admin</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="admin email (e.g. someone@mitgurukul.com)"
                value={addingEmail}
                onChange={(e) => setAddingEmail(e.target.value)}
                data-testid="add-admin-email-input"
                className="flex-1"
              />
              <Input
                placeholder="display name (optional)"
                value={addingName}
                onChange={(e) => setAddingName(e.target.value)}
                data-testid="add-admin-name-input"
                className="flex-1"
              />
              <Button
                onClick={addNew}
                disabled={!addingEmail || savingEmail === addingEmail.toLowerCase()}
                data-testid="add-admin-btn"
                className="bg-[#E97832] hover:bg-[#d56b2c]"
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};



const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [departmentHierarchy, setDepartmentHierarchy] = useState({});
  // Main + Sub ticket-resolving departments — sourced from /api/config/ticket-form
  // so the Manage Users → Department Categories card mirrors EXACTLY the
  // dropdown options on the Ticket Creator UI form (single source of truth).
  const [mainResolvingDepartments, setMainResolvingDepartments] = useState({
    main: [],
    sub: {},
  });
  const [loading, setLoading] = useState(true);
  const [hasEditAccess, setHasEditAccess] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Edit user dialog
  const [editingUser, setEditingUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '', department: '', departments: [], deptCount: 1 });
  const [editLoading, setEditLoading] = useState(false);

  // Resolving Sub-Department CRUD (Apr 28, 2026)
  const [resolvingSubDialog, setResolvingSubDialog] = useState(null);
  // Shape: { mode: 'add' | 'edit', main: string, oldName?: string, value: string, loading?: bool }
  const [resolvingSubDelete, setResolvingSubDelete] = useState(null); // { main, sub }
  const [resolvingSubBusy, setResolvingSubBusy] = useState(false);

  // Feb 2026: per-row dry-run authorization probes.
  //   Shape: { [userId]: { state: 'idle' | 'checking' | 'ok' | 'fail', error?: string } }
  // Populated when the admin clicks the "Test access" button next to a
  // user row. Result stays until the page reloads so the admin can scan
  // the whole table at a glance.
  const [accessProbes, setAccessProbes] = useState({});

  const runAccessProbe = useCallback(async (userRow) => {
    if (!userRow?.email) return;
    setAccessProbes((prev) => ({
      ...prev,
      [userRow.user_id]: { state: 'checking' },
    }));
    try {
      const { data } = await userAPI.validateTicketAccess(userRow.email);
      const passed = !!(data && data.valid);
      setAccessProbes((prev) => ({
        ...prev,
        [userRow.user_id]: passed
          ? { state: 'ok' }
          : { state: 'fail', error: (data && data.error) || 'Not authorized' },
      }));
      if (passed) {
        toast.success(`${userRow.name || userRow.email} is authorised to submit tickets.`);
      } else {
        toast.error((data && data.error) || 'User is not authorised.');
      }
    } catch (err) {
      // Defensive: swallow every possible error path so an axios or
      // network failure never bubbles up to React's ErrorBoundary and
      // crashes the entire /users page. Extract message safely from
      // varying error shapes.
      let errMsg = 'Access check failed';
      try {
        errMsg = (err && err.response && err.response.data && (err.response.data.detail || err.response.data.error))
          || (err && err.message)
          || 'Access check failed';
        if (typeof errMsg !== 'string') errMsg = 'Access check failed';
      } catch (_) {
        errMsg = 'Access check failed';
      }
      setAccessProbes((prev) => ({
        ...prev,
        [userRow.user_id]: { state: 'fail', error: errMsg },
      }));
      try { toast.error(errMsg); } catch (_) { /* toast may be unmounted mid-render */ }
    }
  }, []);

  // Bulk probe — parallel, throttled to 5 concurrent requests so we don't
  // hammer the backend after importing 50+ users from a spreadsheet.
  const [bulkProbeState, setBulkProbeState] = useState({ running: false, done: 0, total: 0 });
  const runAccessProbeBulk = useCallback(async (userList) => {
    const candidates = (userList || []).filter((u) => u?.email);
    if (candidates.length === 0) return;
    setBulkProbeState({ running: true, done: 0, total: candidates.length });
    // Pre-mark every row as "checking" so the UI reflects the queue.
    setAccessProbes((prev) => {
      const next = { ...prev };
      for (const u of candidates) next[u.user_id] = { state: 'checking' };
      return next;
    });

    const CONCURRENCY = 5;
    let cursor = 0;
    let okCount = 0;
    let failCount = 0;
    const worker = async () => {
      while (cursor < candidates.length) {
        const idx = cursor;
        cursor += 1;
        const userRow = candidates[idx];
        try {
          const { data } = await userAPI.validateTicketAccess(userRow.email);
          const passed = !!(data && data.valid);
          if (passed) okCount += 1; else failCount += 1;
          setAccessProbes((prev) => ({
            ...prev,
            [userRow.user_id]: passed
              ? { state: 'ok' }
              : { state: 'fail', error: (data && data.error) || 'Not authorized' },
          }));
        } catch (err) {
          // Same defensive envelope as runAccessProbe — never let one
          // bad row abort the whole batch or crash the page.
          failCount += 1;
          let errMsg = 'Access check failed';
          try {
            errMsg = (err && err.response && err.response.data && (err.response.data.detail || err.response.data.error))
              || (err && err.message)
              || 'Access check failed';
            if (typeof errMsg !== 'string') errMsg = 'Access check failed';
          } catch (_) {
            errMsg = 'Access check failed';
          }
          setAccessProbes((prev) => ({
            ...prev,
            [userRow.user_id]: { state: 'fail', error: errMsg },
          }));
        } finally {
          setBulkProbeState((s) => ({ ...s, done: s.done + 1 }));
        }
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, candidates.length) }, worker)
    );
    setBulkProbeState({ running: false, done: candidates.length, total: candidates.length });
    if (failCount === 0) {
      toast.success(`All ${okCount} users are authorised.`);
    } else {
      toast.error(`${failCount} user${failCount === 1 ? '' : 's'} not authorised. ${okCount} passed. See red rows for details.`);
    }
  }, []);

  // Add user dialog
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ name: '', email: '', role: '', department: '', departments: [], deptCount: 1 });
  const [addUserLoading, setAddUserLoading] = useState(false);

  // Delete user dialog
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  // Department dialogs
  const [showAddDeptDialog, setShowAddDeptDialog] = useState(false);
  const [addDeptCategory, setAddDeptCategory] = useState('');
  const [addDeptName, setAddDeptName] = useState('');
  const [addDeptLoading, setAddDeptLoading] = useState(false);

  const [showEditDeptDialog, setShowEditDeptDialog] = useState(false);
  const [editDeptOldName, setEditDeptOldName] = useState('');
  const [editDeptNewName, setEditDeptNewName] = useState('');
  const [editDeptLoading, setEditDeptLoading] = useState(false);

  const [showDeleteDeptDialog, setShowDeleteDeptDialog] = useState(false);
  const [deleteDeptName, setDeleteDeptName] = useState('');
  const [deleteDeptLoading, setDeleteDeptLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [deptSearchQuery, setDeptSearchQuery] = useState('');

  // Problem Locations
  const [locations, setLocations] = useState([]);
  const [locSearchQuery, setLocSearchQuery] = useState('');
  const [showAddLocDialog, setShowAddLocDialog] = useState(false);
  const [newLocName, setNewLocName] = useState('');
  const [locSaving, setLocSaving] = useState(false);
  const [editLoc, setEditLoc] = useState(null);  // {id, name} or null
  const [editLocName, setEditLocName] = useState('');
  const [deleteLoc, setDeleteLoc] = useState(null);

  const loadLocations = useCallback(async () => {
    try {
      const res = await adminMgmtAPI.getProblemLocations();
      setLocations(res.data || []);
    } catch (error) {
      // Non-admin visitors legitimately receive 403 here — no toast to avoid
      // false alarms. Non-403 failures are logged for debuggability.
      const status = error?.response?.status;
      if (status && status !== 403 && process.env.NODE_ENV !== 'production') {
        console.debug('loadLocations failed:', status, error?.message);
      }
    }
  }, []);

  const loadData = useCallback(async () => {
    const minDelay = new Promise((resolve) => setTimeout(resolve, 500));
    try {
      const [usersRes, deptsRes, accessRes, meRes, ticketFormRes] = await Promise.all([
        userAPI.getUsers(),
        configAPI.getDepartmentsHierarchy(),
        adminMgmtAPI.checkAccess().catch(() => ({ data: { has_edit_access: false } })),
        authAPI.getMe(),
        configAPI.getTicketFormConfig().catch(() => ({ data: { main_departments: [], sub_departments: {} } })),
      ]);
      await minDelay;
      setUsers(usersRes.data);
      setDepartmentHierarchy(deptsRes.data);
      setHasEditAccess(accessRes.data.has_edit_access);
      setCurrentUser(meRes.data);
      setMainResolvingDepartments({
        main: ticketFormRes.data?.main_departments || [],
        sub: ticketFormRes.data?.sub_departments || {},
      });
      loadLocations();
    } catch (error) {
      await minDelay;
      if (process.env.NODE_ENV !== 'production') {
        console.debug('UserManagement loadData failed (silent):', error?.message);
      }
    } finally {
      setLoading(false);
    }
  }, [loadLocations]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ─── Problem Location Handlers ──────────────────────────────────────────────
  const handleAddLocation = async () => {
    const name = newLocName.trim();
    if (!name) { toast.error('Enter a location name'); return; }
    setLocSaving(true);
    try {
      await adminMgmtAPI.addProblemLocation({ name });
      toast.success(`Location '${name}' added`);
      setShowAddLocDialog(false);
      setNewLocName('');
      loadLocations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add location');
    } finally {
      setLocSaving(false);
    }
  };

  const handleEditLocation = async () => {
    const name = editLocName.trim();
    if (!name || !editLoc) return;
    setLocSaving(true);
    try {
      await adminMgmtAPI.editProblemLocation(editLoc.id, { new_name: name });
      toast.success(`Location renamed to '${name}'`);
      setEditLoc(null);
      setEditLocName('');
      loadLocations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to rename location');
    } finally {
      setLocSaving(false);
    }
  };

  const handleDeleteLocation = async () => {
    if (!deleteLoc) return;
    setLocSaving(true);
    try {
      await adminMgmtAPI.deleteProblemLocation(deleteLoc.id);
      toast.success(`Location '${deleteLoc.name}' deleted`);
      setDeleteLoc(null);
      loadLocations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete location');
    } finally {
      setLocSaving(false);
    }
  };

  // ─── User Handlers ──────────────────────────────────────────────────────────

  const handleEditClick = (u) => {
    setEditingUser(u);
    const existingDepts = Array.isArray(u.departments) && u.departments.length > 0
      ? u.departments
      : (u.department ? [u.department] : []);
    setEditForm({
      name: u.name,
      email: u.email,
      role: u.role,
      department: u.department || '',
      departments: existingDepts,
      deptCount: Math.max(1, existingDepts.length || 1),
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    if (editForm.role === 'process_owner') {
      const selected = (editForm.departments || []).filter((d) => d && d.trim());
      const dedup = Array.from(new Set(selected));
      if (dedup.length === 0) {
        toast.error('Please select at least one department for Process Owner');
        return;
      }
      if (dedup.length !== selected.length) {
        toast.error('Duplicate departments detected. Please pick distinct departments.');
        return;
      }
      if (dedup.length !== editForm.deptCount) {
        toast.error(`Please select exactly ${editForm.deptCount} department${editForm.deptCount > 1 ? 's' : ''}`);
        return;
      }
    }
    setEditLoading(true);
    try {
      const payload = {};
      if (editForm.name !== editingUser.name) payload.name = editForm.name;
      if (editForm.email !== editingUser.email) payload.email = editForm.email;
      if (editForm.role !== editingUser.role) payload.role = editForm.role;
      if (editForm.role === 'process_owner') {
        payload.departments = editForm.departments.filter((d) => d && d.trim());
      } else {
        payload.departments = [];
        payload.department = null;
      }
      if (hasEditAccess) {
        await adminMgmtAPI.editUser(editingUser.user_id, payload);
      } else {
        // Non-admin CAOs can only change role + single department via legacy endpoint
        await userAPI.updateUserRole(
          editingUser.user_id,
          editForm.role,
          editForm.role === 'process_owner' ? (payload.departments?.[0] || null) : null,
        );
      }
      toast.success(`${editForm.name} updated successfully!`);
      setShowEditDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!addUserForm.name.trim()) { toast.error('User Name is required'); return; }
    if (!addUserForm.email.trim()) { toast.error('Email is required'); return; }
    if (!addUserForm.role) { toast.error('Role must be selected'); return; }
    if (addUserForm.role === 'process_owner') {
      const selected = (addUserForm.departments || []).filter((d) => d && d.trim());
      const dedup = Array.from(new Set(selected));
      if (dedup.length === 0) {
        toast.error('Please select at least one department for Process Owner');
        return;
      }
      if (dedup.length !== selected.length) {
        toast.error('Duplicate departments detected. Please pick distinct departments.');
        return;
      }
      if (dedup.length !== addUserForm.deptCount) {
        toast.error(`Please select exactly ${addUserForm.deptCount} department${addUserForm.deptCount > 1 ? 's' : ''}`);
        return;
      }
    }
    setAddUserLoading(true);
    try {
      const payload = {
        name: addUserForm.name.trim(),
        email: addUserForm.email.trim().toLowerCase(),
        role: addUserForm.role,
      };
      if (addUserForm.role === 'process_owner') {
        payload.departments = addUserForm.departments.filter((d) => d && d.trim());
      }
      await adminMgmtAPI.addUser(payload);
      toast.success(`User '${addUserForm.name}' added successfully!`);
      setShowAddUserDialog(false);
      setAddUserForm({ name: '', email: '', role: '', department: '', departments: [], deptCount: 1 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    setDeleteUserLoading(true);
    try {
      await adminMgmtAPI.deleteUser(deleteUser.user_id);
      toast.success(`User '${deleteUser.name}' deleted`);
      setDeleteUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteUserLoading(false);
    }
  };

  // ─── Department Handlers ────────────────────────────────────────────────────

  const handleAddDept = async () => {
    if (!addDeptName.trim()) { toast.error('Department name is required'); return; }
    setAddDeptLoading(true);
    try {
      await adminMgmtAPI.addDepartment({ name: addDeptName.trim(), category: addDeptCategory });
      toast.success(`Department '${addDeptName}' added!`);
      setShowAddDeptDialog(false);
      setAddDeptName('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add department');
    } finally {
      setAddDeptLoading(false);
    }
  };

  const handleEditDept = async () => {
    if (!editDeptNewName.trim()) { toast.error('New name is required'); return; }
    if (editDeptNewName === editDeptOldName) { toast.error('Name is unchanged'); return; }
    setEditDeptLoading(true);
    try {
      await adminMgmtAPI.editDepartment(editDeptOldName, { new_name: editDeptNewName.trim() });
      toast.success(`Department renamed to '${editDeptNewName}'`);
      setShowEditDeptDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename department');
    } finally {
      setEditDeptLoading(false);
    }
  };

  const handleDeleteDept = async () => {
    setDeleteDeptLoading(true);
    try {
      await adminMgmtAPI.deleteDepartment(deleteDeptName);
      toast.success(`Department '${deleteDeptName}' deleted`);
      setShowDeleteDeptDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete department');
    } finally {
      setDeleteDeptLoading(false);
    }
  };

  // ─── Resolving Sub-Department CRUD ──────────────────────────────────────────
  // Refresh the ticket-form-driven list after every successful mutation so the
  // Manage Users card and the TC UI stay in sync without a hard reload.
  const refreshTicketFormConfig = useCallback(async () => {
    try {
      const res = await configAPI.getTicketFormConfig();
      setMainResolvingDepartments({
        main: res.data?.main_departments || [],
        sub: res.data?.sub_departments || {},
      });
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('refreshTicketFormConfig failed:', e?.message);
      }
    }
  }, []);

  const handleSaveResolvingSub = async () => {
    if (!resolvingSubDialog) return;
    const value = (resolvingSubDialog.value || '').trim();
    if (!value) { toast.error('Sub-department name cannot be empty.'); return; }
    setResolvingSubBusy(true);
    try {
      if (resolvingSubDialog.mode === 'add') {
        await adminMgmtAPI.addResolvingSub(resolvingSubDialog.main, value);
        toast.success(`Added "${value}" under ${resolvingSubDialog.main}.`);
      } else {
        await adminMgmtAPI.renameResolvingSub(
          resolvingSubDialog.main,
          resolvingSubDialog.oldName,
          value,
        );
        toast.success(`Renamed "${resolvingSubDialog.oldName}" → "${value}".`);
      }
      setResolvingSubDialog(null);
      await refreshTicketFormConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save the sub-department.');
    } finally {
      setResolvingSubBusy(false);
    }
  };

  const handleDeleteResolvingSub = async () => {
    if (!resolvingSubDelete) return;
    setResolvingSubBusy(true);
    try {
      await adminMgmtAPI.deleteResolvingSub(resolvingSubDelete.main, resolvingSubDelete.sub);
      toast.success(`Removed "${resolvingSubDelete.sub}" from ${resolvingSubDelete.main}.`);
      setResolvingSubDelete(null);
      await refreshTicketFormConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not delete the sub-department.');
    } finally {
      setResolvingSubBusy(false);
    }
  };

  // ─── Helpers ────────────────────────────────────────────────────────────────

  // Memoized lookup: { "Electrical Dept": "Administration & Infrastructure", … }
  // Avoids a linear scan on every JSX render of the users table.
  const departmentCategoryMap = useMemo(() => {
    const out = {};
    Object.entries(departmentHierarchy).forEach(([category, data]) => {
      (data?.departments || []).forEach((dept) => { out[dept] = category; });
    });
    return out;
  }, [departmentHierarchy]);

  const getDepartmentCategory = (dept) => departmentCategoryMap[dept] || null;

  const getRoleBadge = (role) => {
    const variants = {
      'cao': 'bg-purple-100 text-purple-800',
      'process_owner': 'bg-blue-100 text-blue-800',
      'parent_staff': 'bg-slate-100 text-slate-800',
      'student': 'bg-green-100 text-green-800',
    };
    const labels = { 'cao': 'CAO', 'process_owner': 'Process Owner', 'parent_staff': 'Parent/Staff', 'student': 'Student' };
    return <Badge className={variants[role] || variants['parent_staff']}>{labels[role] || role}</Badge>;
  };

  const getRoleLabel = (role) => {
    const labels = { 'cao': 'CAO', 'process_owner': 'Process Owner', 'parent_staff': 'Parent/Staff', 'student': 'Student' };
    return labels[role] || role;
  };

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.department || '').toLowerCase().includes(q),
    );
  }, [users, searchQuery]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-[#E97832] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <Button data-testid="back-btn" variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2">
            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-heading font-bold text-slate-900">User Management</h1>
              <p className="text-xs sm:text-sm text-slate-600">Assign roles and departments</p>
            </div>
            {hasEditAccess && (
              <Badge className="bg-green-100 text-green-800 text-xs flex-shrink-0">Full Edit Access</Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 space-y-4 sm:space-y-6">
        {/* ──── Main Ticket Resolving Department ──── */}
        {/* Shows the four Main Ticket Resolving Departments and their Sub
            Ticket Resolving Departments. Authorized admins can add, rename
            and delete subs; changes are persisted in MongoDB and propagate
            instantly to the Ticket Creator UI ticket-creation form and the
            Ticket Diverted dropdown via /api/config/ticket-form &
            /api/config/divert-departments. The four mains themselves are
            anchored constants and intentionally not editable. */}
        <Card data-testid="main-resolving-departments-card">
          <CardHeader>
            <CardTitle className="font-heading text-base">Main Ticket Resolving Department</CardTitle>
            <CardDescription>
              Live mirror of the Sub Ticket Resolving Department dropdown on the Ticket Creator UI ticket-creation form. Each main category lists every sub-department a creator can choose for that area. Edits propagate to the ticket form and the Ticket Diverted dropdown automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {mainResolvingDepartments.main.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-sm">Loading ticket form configuration…</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mainResolvingDepartments.main.map((mainDept) => {
                  const subs = mainResolvingDepartments.sub?.[mainDept] || [];
                  return (
                    <div
                      key={mainDept}
                      data-testid={`main-resolving-dept-${mainDept.toLowerCase().replace(/\s+/g, '-')}`}
                      className="border border-slate-200 rounded-lg p-4 bg-white"
                    >
                      <div className="flex items-center justify-between mb-3 gap-2">
                        <h4 className="font-semibold text-[#E97832]">{mainDept}</h4>
                        <Badge className="bg-slate-100 text-slate-700 text-xs">
                          {subs.length} sub-{subs.length === 1 ? 'department' : 'departments'}
                        </Badge>
                      </div>
                      {subs.length === 0 ? (
                        <p className="text-xs text-slate-400">No sub-departments configured.</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {subs.map((sub) => (
                            <div key={sub} className="group relative inline-flex items-center">
                              <Badge
                                variant="outline"
                                className="text-xs border-slate-300 text-slate-700 bg-slate-50 pr-1"
                                data-testid={`sub-resolving-dept-${sub.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                {sub}
                                {hasEditAccess && (
                                  <span className="ml-1 inline-flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      type="button"
                                      onClick={() => setResolvingSubDialog({ mode: 'edit', main: mainDept, oldName: sub, value: sub })}
                                      className="p-0.5 hover:text-blue-600"
                                      data-testid={`edit-resolving-sub-${sub.toLowerCase().replace(/\s+/g, '-')}`}
                                      title={`Rename ${sub}`}
                                    >
                                      <Pencil className="w-2.5 h-2.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setResolvingSubDelete({ main: mainDept, sub })}
                                      className="p-0.5 hover:text-red-600"
                                      data-testid={`delete-resolving-sub-${sub.toLowerCase().replace(/\s+/g, '-')}`}
                                      title={`Delete ${sub}`}
                                    >
                                      <Trash2 className="w-2.5 h-2.5" />
                                    </button>
                                  </span>
                                )}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                      {hasEditAccess && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 text-xs text-[#E97832] border-[#E97832]/30 hover:bg-orange-50"
                          onClick={() => setResolvingSubDialog({ mode: 'add', main: mainDept, value: '' })}
                          data-testid={`add-resolving-sub-${mainDept.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Sub-Department
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ──── Main Department Admin Access ──── */}
        {/* Per-admin → main-department mapping that drives CC email
            routing + SLA breach escalations. Admins can edit which main
            departments they own at Level 1 (CC) and Level 2 (second
            escalation tier). CAO's "fallback" toggle catches any main
            department not explicitly assigned to anyone. */}
        <MainDeptAdminAccessCard hasEditAccess={hasEditAccess} />


        {/* ──── Problem Locations ──── */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="font-heading">Problem Locations</CardTitle>
                <CardDescription>
                  Locations shown in the ticket form's "Location of the Problem" dropdown.
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search locations..."
                    value={locSearchQuery}
                    onChange={(e) => setLocSearchQuery(e.target.value)}
                    className="pl-8 w-56"
                    data-testid="location-search-input"
                  />
                </div>
                {hasEditAccess && (
                  <Button
                    onClick={() => { setNewLocName(''); setShowAddLocDialog(true); }}
                    className="bg-[#E97832] hover:bg-[#C25E20] text-white"
                    data-testid="add-location-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Location
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {locations.length === 0 ? (
              <p className="text-sm text-slate-500 italic">No locations configured yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="locations-list">
                {locations
                  .filter((l) => !locSearchQuery.trim() || l.name.toLowerCase().includes(locSearchQuery.toLowerCase()))
                  .map((loc) => (
                    <div key={loc.id} className="group relative inline-flex items-center">
                      <Badge variant="outline" className="text-xs pr-1" data-testid={`location-badge-${loc.id}`}>
                        {loc.name}
                        {hasEditAccess && (
                          <span className="ml-1 inline-flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => { setEditLoc(loc); setEditLocName(loc.name); }}
                              className="p-0.5 hover:text-blue-600"
                              data-testid={`edit-location-${loc.id}`}
                              title="Edit"
                            >
                              <Pencil className="w-2.5 h-2.5" />
                            </button>
                            <button
                              onClick={() => setDeleteLoc(loc)}
                              className="p-0.5 hover:text-red-600"
                              data-testid={`delete-location-${loc.id}`}
                              title="Delete"
                            >
                              <Trash2 className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        )}
                      </Badge>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="font-heading">All Users</CardTitle>
                <CardDescription>
                  Click the edit button to assign roles and departments. Process Owners must be assigned to a specific department.
                </CardDescription>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 w-56"
                    data-testid="user-search-input"
                  />
                </div>

                {/*
                  Feb 2026: bulk access probe — runs a parallel (5 at a
                  time) dry-run authorization check for every visible
                  user. Failing rows turn red so admins spot broken
                  provisioning after a spreadsheet import at a glance.
                */}
                <Button
                  variant="outline"
                  onClick={() => runAccessProbeBulk(filteredUsers)}
                  disabled={bulkProbeState.running || filteredUsers.length === 0}
                  className="flex items-center gap-2"
                  data-testid="test-all-access-btn"
                >
                  {bulkProbeState.running ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {`Testing ${bulkProbeState.done}/${bulkProbeState.total}…`}
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4" />
                      Test all
                    </>
                  )}
                </Button>

                {hasEditAccess && (
                  <Button
                    onClick={() => {
                      setAddUserForm({ name: '', email: '', role: '', department: '', departments: [], deptCount: 1 });
                      setShowAddUserDialog(true);
                    }}
                    className="bg-[#E97832] hover:bg-[#C25E20] text-white"
                    data-testid="add-user-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-sm text-slate-700">User</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-slate-700">Email</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-slate-700">Current Role</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-slate-700">Department</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => {
                    const probeState = accessProbes[u.user_id]?.state;
                    const rowFailedProbe = probeState === 'fail';
                    return (
                    <tr
                      key={u.user_id}
                      data-testid={`user-row-${u.user_id}`}
                      className={`border-b border-slate-100 ${
                        rowFailedProbe
                          ? 'bg-red-50 hover:bg-red-100'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                              <User className="w-5 h-5 text-slate-500" />
                            </div>
                          )}
                          <span className="font-medium text-slate-900">{u.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Mail className="w-4 h-4" />
                          {u.email}
                        </div>
                      </td>
                      <td className="py-4 px-4">{getRoleBadge(u.role)}</td>
                      <td className="py-4 px-4">
                        {(() => {
                          const depts = (Array.isArray(u.departments) && u.departments.length > 0)
                            ? u.departments
                            : (u.department ? [u.department] : []);
                          if (depts.length === 0) {
                            return <span className="text-slate-400 italic text-sm">Not assigned</span>;
                          }
                          return (
                            <div className="flex flex-wrap gap-1 max-w-[260px]">
                              {depts.map((d) => (
                                <Badge
                                  key={d}
                                  variant="outline"
                                  className="text-xs border-blue-200 text-blue-800 bg-blue-50"
                                >
                                  {d}
                                </Badge>
                              ))}
                              {depts.length === 1 && getDepartmentCategory(depts[0]) && (
                                <span className="block w-full text-xs text-slate-500">
                                  {getDepartmentCategory(depts[0])}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditClick(u)}
                            className="flex items-center gap-2"
                            data-testid={`edit-user-${u.user_id}`}
                          >
                            <Edit className="w-3 h-3" />
                            Edit
                          </Button>

                          {/*
                            Feb 2026: dry-run authorization probe. Sends
                            the user's email to /api/validate-email and
                            surfaces the result inline so admins can
                            verify a newly-added user is fully wired up
                            without asking them to try creating a ticket.
                          */}
                          {(() => {
                            const probe = accessProbes[u.user_id] || { state: 'idle' };
                            const stateStyles = {
                              idle: 'border-slate-300 text-slate-700 hover:bg-slate-50',
                              checking: 'border-slate-300 text-slate-500 cursor-wait',
                              ok: 'border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100',
                              fail: 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100',
                            }[probe.state];
                            const Icon = {
                              idle: ShieldCheck,
                              checking: Loader2,
                              ok: ShieldCheck,
                              fail: ShieldAlert,
                            }[probe.state];
                            const label = {
                              idle: 'Test access',
                              checking: 'Checking…',
                              ok: 'Authorised',
                              fail: 'Not authorised',
                            }[probe.state];
                            return (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => runAccessProbe(u)}
                                disabled={probe.state === 'checking'}
                                title={probe.state === 'fail' ? probe.error : 'Simulate a ticket-submission access check for this user'}
                                className={`flex items-center gap-2 ${stateStyles}`}
                                data-testid={`test-access-${u.user_id}`}
                              >
                                <Icon
                                  className={`w-3 h-3 ${probe.state === 'checking' ? 'animate-spin' : ''}`}
                                />
                                {label}
                              </Button>
                            );
                          })()}

                          {hasEditAccess && currentUser?.user_id !== u.user_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDeleteUser(u)}
                              className="flex items-center gap-2 border-red-200 text-red-700 hover:bg-red-50"
                              data-testid={`delete-user-${u.user_id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredUsers.length === 0 && (
              <div className="text-center py-12 text-slate-500"><p>No users found</p></div>
            )}
          </CardContent>
        </Card>

        {/* Role Descriptions */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-base">Role Descriptions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <Shield className="w-5 h-5 text-purple-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-purple-900">CAO</h4>
                <p className="text-sm text-purple-700">Full access to War Room dashboard, analytics, user management, and all tickets.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900">Process Owner</h4>
                <p className="text-sm text-blue-700">Can view and update tickets for their assigned department. Must specify department.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <Shield className="w-5 h-5 text-slate-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-slate-900">Parent/Staff</h4>
                <p className="text-sm text-slate-700">Can create tickets and view their own submitted complaints only.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <Shield className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-green-900">Student</h4>
                <p className="text-sm text-green-700">Can create tickets and view their own submitted complaints only.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* ──── Edit User Dialog ──── */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {hasEditAccess ? 'Edit User' : 'Edit User Role'}
            </DialogTitle>
            <DialogDescription>
              {hasEditAccess
                ? `Edit details for ${editingUser?.name}`
                : `Assign role and department for ${editingUser?.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {hasEditAccess && (
              <>
                <div className="space-y-2">
                  <Label>User Name</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                    data-testid="edit-user-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email ID</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                    data-testid="edit-user-email"
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Select Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm((p) => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="edit-role-select">
                  <SelectValue placeholder="Choose a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="parent_staff">Parent/Staff</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="process_owner">Process Owner</SelectItem>
                  <SelectItem value="cao">CAO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editForm.role === 'process_owner' && (
              <MultiDepartmentPickerStable
                count={editForm.deptCount}
                departments={editForm.departments}
                onCountChange={(n) => setEditForm((p) => ({ ...p, deptCount: n }))}
                onDepartmentsChange={(arr) => setEditForm((p) => ({ ...p, departments: arr, department: arr[0] || '' }))}
                testIdPrefix="edit-user"
                departmentHierarchy={departmentHierarchy}
                deptSearchQuery={deptSearchQuery}
                setDeptSearchQuery={setDeptSearchQuery}
              />
            )}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Summary</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p><strong>User:</strong> {editForm.name}</p>
                <p><strong>Email:</strong> {editForm.email}</p>
                <p><strong>Role:</strong> {getRoleLabel(editForm.role)}</p>
                {editForm.role === 'process_owner' && (
                  <p>
                    <strong>Departments ({editForm.deptCount}):</strong>{' '}
                    {(editForm.departments || []).filter(Boolean).length > 0
                      ? (editForm.departments || []).filter(Boolean).join(', ')
                      : <span className="text-red-600">Not selected</span>}
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveEdit}
              disabled={editLoading}
              className="bg-gradient-to-r from-[#E97832] to-[#EA580C] hover:from-[#C25E20] hover:to-[#C2410C]"
              data-testid="save-edit-btn"
            >
              {editLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Add User Dialog ──── */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add New User</DialogTitle>
            <DialogDescription>Manually add a new user to the system</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>User Name <span className="text-red-500">*</span></Label>
              <Input
                placeholder="Enter full name"
                value={addUserForm.name}
                onChange={(e) => setAddUserForm((p) => ({ ...p, name: e.target.value }))}
                data-testid="add-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email ID <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                placeholder={
                  addUserForm.role === 'parent_staff'
                    ? 'any email (personal address allowed for parents)'
                    : 'user@mitgurukul.com'
                }
                value={addUserForm.email}
                onChange={(e) => setAddUserForm((p) => ({ ...p, email: e.target.value }))}
                data-testid="add-user-email"
              />
              {addUserForm.role === 'parent_staff' && (
                <p className="text-xs text-slate-500 mt-1">
                  Parents and non-employee staff may use any email domain
                  — the @mitgurukul.com school domain is optional for this role.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Role <span className="text-red-500">*</span></Label>
              <Select value={addUserForm.role} onValueChange={(v) => setAddUserForm((p) => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="add-user-role-select">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="parent_staff">Parent/Staff</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="process_owner">Process Owner</SelectItem>
                  <SelectItem value="cao">CAO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {addUserForm.role === 'process_owner' && (
              <MultiDepartmentPickerStable
                count={addUserForm.deptCount}
                departments={addUserForm.departments}
                onCountChange={(n) => setAddUserForm((p) => ({ ...p, deptCount: n }))}
                onDepartmentsChange={(arr) => setAddUserForm((p) => ({ ...p, departments: arr, department: arr[0] || '' }))}
                testIdPrefix="add-user"
                departmentHierarchy={departmentHierarchy}
                deptSearchQuery={deptSearchQuery}
                setDeptSearchQuery={setDeptSearchQuery}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUserDialog(false)}>Cancel</Button>
            <Button
              onClick={handleAddUser}
              disabled={addUserLoading}
              className="bg-[#E97832] hover:bg-[#C25E20] text-white"
              data-testid="add-user-submit-btn"
            >
              {addUserLoading ? 'Adding...' : 'Add User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Add Department Dialog ──── */}
      <Dialog open={showAddDeptDialog} onOpenChange={setShowAddDeptDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Department</DialogTitle>
            <DialogDescription>Add a new department under <strong>{addDeptCategory}</strong></DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Input value={addDeptCategory} disabled className="bg-slate-50" />
            </div>
            <div className="space-y-2">
              <Label>Department Name <span className="text-red-500">*</span></Label>
              <Input
                placeholder="Enter department name"
                value={addDeptName}
                onChange={(e) => setAddDeptName(e.target.value)}
                data-testid="add-dept-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDeptDialog(false)}>Cancel</Button>
            <Button
              onClick={handleAddDept}
              disabled={addDeptLoading}
              className="bg-[#E97832] hover:bg-[#C25E20] text-white"
              data-testid="add-dept-submit-btn"
            >
              {addDeptLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Edit Department Dialog ──── */}
      <Dialog open={showEditDeptDialog} onOpenChange={setShowEditDeptDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Rename Department</DialogTitle>
            <DialogDescription>Rename "{editDeptOldName}"</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Name <span className="text-red-500">*</span></Label>
              <Input
                value={editDeptNewName}
                onChange={(e) => setEditDeptNewName(e.target.value)}
                data-testid="edit-dept-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDeptDialog(false)}>Cancel</Button>
            <Button
              onClick={handleEditDept}
              disabled={editDeptLoading}
              className="bg-[#E97832] hover:bg-[#C25E20] text-white"
              data-testid="edit-dept-submit-btn"
            >
              {editDeptLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Delete Department Dialog ──── */}
      <Dialog open={showDeleteDeptDialog} onOpenChange={setShowDeleteDeptDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Delete Department
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "<strong>{deleteDeptName}</strong>"? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            If active tickets are mapped to this department, deletion will be blocked.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDeptDialog(false)}>Cancel</Button>
            <Button
              onClick={handleDeleteDept}
              disabled={deleteDeptLoading}
              variant="destructive"
              data-testid="delete-dept-confirm-btn"
            >
              {deleteDeptLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Problem Location Dialogs ──── */}
      <Dialog open={showAddLocDialog} onOpenChange={setShowAddLocDialog}>
        <DialogContent className="sm:max-w-[400px]" data-testid="add-location-dialog">
          <DialogHeader>
            <DialogTitle>Add Problem Location</DialogTitle>
            <DialogDescription>New locations appear in the ticket form's "Location of the Problem" dropdown.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-location-name">Location Name *</Label>
            <Input
              id="new-location-name"
              data-testid="new-location-input"
              value={newLocName}
              onChange={(e) => setNewLocName(e.target.value)}
              placeholder="e.g. 4/1 PYP School"
              onKeyDown={(e) => e.key === 'Enter' && handleAddLocation()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLocDialog(false)} disabled={locSaving}>Cancel</Button>
            <Button
              onClick={handleAddLocation} disabled={locSaving || !newLocName.trim()}
              className="bg-[#E97832] hover:bg-[#C25E20]" data-testid="save-location-btn"
            >
              {locSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editLoc} onOpenChange={(o) => { if (!o) setEditLoc(null); }}>
        <DialogContent className="sm:max-w-[400px]" data-testid="edit-location-dialog">
          <DialogHeader>
            <DialogTitle>Edit Problem Location</DialogTitle>
            <DialogDescription>Existing tickets referencing this location will be renamed automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="edit-location-name">Location Name *</Label>
            <Input
              id="edit-location-name"
              data-testid="edit-location-input"
              value={editLocName}
              onChange={(e) => setEditLocName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleEditLocation()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLoc(null)} disabled={locSaving}>Cancel</Button>
            <Button
              onClick={handleEditLocation} disabled={locSaving || !editLocName.trim()}
              className="bg-[#E97832] hover:bg-[#C25E20]" data-testid="save-edit-location-btn"
            >
              {locSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteLoc} onOpenChange={(o) => { if (!o) setDeleteLoc(null); }}>
        <DialogContent className="sm:max-w-[400px]" data-testid="delete-location-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Delete Problem Location
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteLoc?.name}</strong>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteLoc(null)} disabled={locSaving}>Cancel</Button>
            <Button
              variant="destructive" onClick={handleDeleteLocation} disabled={locSaving}
              data-testid="confirm-delete-location-btn"
            >
              {locSaving ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Delete User Dialog ──── */}
      <Dialog open={!!deleteUser} onOpenChange={(o) => { if (!o) setDeleteUser(null); }}>
        <DialogContent className="sm:max-w-[440px]" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700 font-heading">
              <AlertTriangle className="w-5 h-5" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete <strong>{deleteUser?.name}</strong>
              {deleteUser?.email ? ` (${deleteUser.email})` : ''}? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            Any active tickets assigned to this user must be reassigned first. Their sessions
            will be signed out immediately.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUser(null)} disabled={deleteUserLoading}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleteUserLoading}
              data-testid="confirm-delete-user-btn"
            >
              {deleteUserLoading ? 'Deleting…' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Resolving Sub-Department Add/Edit Dialog ──── */}
      <Dialog
        open={!!resolvingSubDialog}
        onOpenChange={(open) => { if (!open) setResolvingSubDialog(null); }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {resolvingSubDialog?.mode === 'edit' ? 'Rename Sub-Department' : 'Add Sub-Department'}
            </DialogTitle>
            <DialogDescription>
              {resolvingSubDialog?.mode === 'edit'
                ? `Rename "${resolvingSubDialog?.oldName}" under ${resolvingSubDialog?.main}.`
                : `Add a new sub-department under ${resolvingSubDialog?.main}. It will appear on the Ticket Creator UI form and the Ticket Diverted dropdown immediately.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Sub-Department Name <span className="text-red-500">*</span></Label>
              <Input
                value={resolvingSubDialog?.value || ''}
                onChange={(e) => setResolvingSubDialog((prev) => ({ ...prev, value: e.target.value }))}
                placeholder="e.g., Robotics Club"
                data-testid="resolving-sub-name-input"
                onKeyDown={(e) => { if (e.key === 'Enter' && !resolvingSubBusy) handleSaveResolvingSub(); }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolvingSubDialog(null)} disabled={resolvingSubBusy}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveResolvingSub}
              disabled={resolvingSubBusy || !(resolvingSubDialog?.value || '').trim()}
              className="bg-[#E97832] hover:bg-[#C25E20] text-white"
              data-testid="resolving-sub-submit-btn"
            >
              {resolvingSubBusy ? 'Saving…' : (resolvingSubDialog?.mode === 'edit' ? 'Save' : 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Resolving Sub-Department Delete Confirm ──── */}
      <Dialog
        open={!!resolvingSubDelete}
        onOpenChange={(open) => { if (!open) setResolvingSubDelete(null); }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete Sub-Department?</DialogTitle>
            <DialogDescription>
              Remove <span className="font-semibold">"{resolvingSubDelete?.sub}"</span> from {resolvingSubDelete?.main}? This will hide it from the Ticket Creator UI form and the Ticket Diverted dropdown. Existing tickets already filed under this sub keep their history.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolvingSubDelete(null)} disabled={resolvingSubBusy}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteResolvingSub}
              disabled={resolvingSubBusy}
              data-testid="confirm-delete-resolving-sub-btn"
            >
              {resolvingSubBusy ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
